import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Eraser, Eye } from 'lucide-react';
import { cn } from '@/common/utils';
import storageService from '../../services/storageService';

interface SignatureCanvasProps {
  onSave: (signature: Blob | null) => void;
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
  existingSignatureUrl?: string;
}

export const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
  onSave,
  disabled = false,
  error,
  label = 'Firma Digital',
  required = true,
  existingSignatureUrl,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [showExistingSignature, setShowExistingSignature] = useState(false);

  // Configurar el canvas inicial
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    // Ajustar el tamaño del canvas al tamaño real del contenedor
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // Guardar el contenido actual antes de redimensionar
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

      // Establecer el tamaño interno del canvas (resolución real)
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      // Escalar el contexto para compensar el devicePixelRatio
      context.scale(dpr, dpr);

      // Configurar el estilo de dibujo
      context.lineCap = 'round';
      context.strokeStyle = '#000';
      context.lineWidth = 2;

      // Restaurar el contenido si existía
      if (imageData.width > 0 && imageData.height > 0) {
        context.putImageData(imageData, 0, 0);
      }
    };

    resizeCanvas();

    // Observar cambios de tamaño
    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(canvas);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Cargar firma existente usando el servicio de storage
  const loadExistingSignature = async () => {
    if (!existingSignatureUrl) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    try {
      // Obtener el blob usando el servicio de storage
      const blobUrl = await storageService.createObjectUrl(existingSignatureUrl);

      const img = new Image();

      img.onload = () => {
        // Limpiar el canvas
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        context.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

        // Calcular el tamaño para mantener la proporción
        const scale = Math.min(rect.width / img.width, rect.height / img.height);
        const x = (rect.width - img.width * scale) / 2;
        const y = (rect.height - img.height * scale) / 2;

        context.drawImage(img, x, y, img.width * scale, img.height * scale);
        setIsEmpty(false);
        setShowExistingSignature(true);

        // Liberar la URL del blob
        URL.revokeObjectURL(blobUrl);
      };

      img.onerror = (error) => {
        console.error('Error al dibujar la imagen en el canvas:', error);
        URL.revokeObjectURL(blobUrl);
        alert('No se pudo dibujar la firma en el canvas.');
      };

      img.src = blobUrl;

    } catch (error: any) {
      console.error('Error al cargar la firma existente:', error);
      console.error('URL:', existingSignatureUrl);

      const errorMessage = error.response?.status === 404
        ? 'No se encontró el archivo de firma.'
        : 'No se pudo cargar la firma. Verifique la consola para más detalles.';

      alert(errorMessage);
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    // Si se está mostrando una firma existente, limpiarla al empezar a dibujar
    if (showExistingSignature) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      setShowExistingSignature(false);
    }

    setIsDrawing(true);
    setIsEmpty(false);

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = canvasRef.current;
      if (canvas && !isEmpty) {
        canvas.toBlob((blob) => {
          if (blob) {
            onSave(blob);
          }
        }, 'image/png');
      }
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    setShowExistingSignature(false);
    onSave(null);
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      <div
        className={cn(
          'relative rounded-lg border-2 border-dashed bg-gray-50',
          error ? 'border-red-300' : 'border-gray-300',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <canvas
          ref={canvasRef}
          className={cn(
            'w-full h-[200px] cursor-crosshair touch-none',
            disabled && 'cursor-not-allowed'
          )}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />

        {isEmpty && !disabled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-gray-400">Dibuje su firma aquí</p>
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      <div className="flex gap-2 flex-wrap">
        {existingSignatureUrl && !showExistingSignature && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadExistingSignature}
            disabled={disabled}
          >
            <Eye className="h-4 w-4 mr-2" />
            Ver firma actual
          </Button>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearSignature}
          disabled={disabled || isEmpty}
        >
          <Eraser className="h-4 w-4 mr-2" />
          Limpiar
        </Button>
      </div>
      <p className="text-xs text-gray-500">La firma se guarda al registrar el retiro.</p>
    </div>
  );
};
